import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@nextui-org/react";
import { format, parseISO } from "date-fns";
import { BsChevronDown, BsPencil } from "react-icons/bs";
import { IoInformationCircleOutline } from "react-icons/io5";
import { MdSummarize } from "react-icons/md";
import TimecodeBadge from "./TimecodeBadge";
import { splitOverviewTimecodes } from "./utils/timecode";

const formatEditedAt = (edited_at) => {
  if (!edited_at) return null;

  try {
    return format(parseISO(edited_at), "dd/MM/yyyy HH:mm");
  } catch (error) {
    return null;
  }
};

const SummaryBlock = ({
  overview,
  overview_timecodes,
  on_seek,
  fallback_text,
  edited_by,
  edited_at,
  can_edit = false,
  on_save,
}) => {
  const { t } = useTranslation();

  const [is_expanded, set_is_expanded] = useState(true);
  const [is_editing, set_is_editing] = useState(false);
  const [draft, set_draft] = useState("");
  const [is_saving, set_is_saving] = useState(false);
  const [has_error, set_has_error] = useState(false);
  const [just_saved, set_just_saved] = useState(false);
  const textarea_ref = useRef(null);

  const text = overview || fallback_text;

  // Switching to another call while the editor is open would otherwise carry
  // the previous call's draft over.
  useEffect(() => {
    set_is_editing(false);
    set_has_error(false);
    set_just_saved(false);
  }, [overview]);

  useEffect(() => {
    if (!is_editing) return;

    textarea_ref.current?.focus();
  }, [is_editing]);

  // Nothing to show and nothing to write: keep the block out of the layout.
  if (!text && !can_edit) return null;

  const edited_at_label = formatEditedAt(edited_at);
  // Links only on the generated summary: a manual edit rewrites the text, so the
  // stored timecoded version would no longer match what is displayed.
  const timecoded_parts =
    !edited_at && on_seek && overview_timecodes ? splitOverviewTimecodes(overview_timecodes) : [];

  const onStartEditing = (event) => {
    event.stopPropagation();

    set_draft(text || "");
    set_has_error(false);
    set_just_saved(false);
    set_is_editing(true);
    set_is_expanded(true);
  };

  const onCancel = () => {
    set_is_editing(false);
    set_has_error(false);
  };

  const onSave = async () => {
    const next_overview = draft.trim();
    if (!next_overview || is_saving) return;

    set_is_saving(true);
    set_has_error(false);

    try {
      await on_save(next_overview);
      set_is_editing(false);
      // Set after awaiting: the parent updating `overview` re-runs the reset
      // effect above, which would otherwise clear this straight away.
      set_just_saved(true);
    } catch (error) {
      console.error("Error saving call overview:", error);
      set_has_error(true);
    }

    set_is_saving(false);
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div
        className={`w-full flex items-center justify-between px-4 py-3 bg-gray-50 ${
          is_expanded ? "border-b border-gray-200" : ""
        }`}
      >
        <button
          type="button"
          onClick={() => set_is_expanded(!is_expanded)}
          className="flex items-center gap-2 flex-1 text-left hover:opacity-70 transition-opacity"
        >
          <MdSummarize
            className="text-gray-400"
            size={20}
          />
          <span className="font-medium text-base">{t("Résumé")}</span>
          <BsChevronDown
            className={`text-gray-400 transition-transform ml-auto ${
              is_expanded ? "rotate-180" : ""
            }`}
            size={16}
          />
        </button>
        {can_edit && !is_editing && (
          <button
            type="button"
            onClick={onStartEditing}
            title={t("Modifier le résumé")}
            aria-label={t("Modifier le résumé")}
            className="ml-3 p-1.5 rounded-md text-gray-400 hover:text-primary hover:bg-gray-200 transition-colors"
          >
            <BsPencil size={14} />
          </button>
        )}
      </div>

      {is_expanded && (
        <div className="p-5">
          {!is_editing && (
            <>
              <div className="max-h-[280px] overflow-y-auto text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                {timecoded_parts.length > 0 &&
                  timecoded_parts.map((part, index) =>
                    part.text !== undefined ? (
                      <React.Fragment key={`t-${index}`}>{part.text}</React.Fragment>
                    ) : (
                      <TimecodeBadge
                        key={`t-${index}`}
                        seconds={part.seconds}
                        on_seek={on_seek}
                        className="ml-1"
                      />
                    )
                  )}
                {timecoded_parts.length === 0 &&
                  (text || <span className="text-gray-400">{t("Aucun résumé")}</span>)}
              </div>
              {edited_by && edited_at_label && (
                <p className="mt-3 text-xs text-gray-400 italic">
                  {t("Modifié par")} {edited_by} · {edited_at_label}
                </p>
              )}
              {just_saved && (
                <div className="mt-3 flex items-start gap-2 rounded-lg bg-primary/5 px-3 py-2 text-xs text-gray-500">
                  <IoInformationCircleOutline
                    size={14}
                    className="mt-[2px] shrink-0"
                  />
                  <span>
                    {t(
                      "Résumé enregistré. La mise à jour peut prendre quelques minutes à apparaître dans la liste des appels et la recherche."
                    )}
                  </span>
                </div>
              )}
            </>
          )}

          {is_editing && (
            <div className="flex flex-col gap-3">
              <textarea
                ref={textarea_ref}
                value={draft}
                onChange={(event) => set_draft(event.target.value)}
                rows={8}
                aria-label={t("Résumé")}
                className="w-full text-sm text-gray-700 leading-relaxed border border-gray-200 rounded-lg p-3 outline-none focus:border-primary resize-y"
              />
              {has_error && <p className="text-xs text-danger">{t("La sauvegarde a échoué")}</p>}
              <div className="flex items-center justify-end gap-2">
                <Button
                  size="sm"
                  variant="light"
                  onPress={onCancel}
                  isDisabled={is_saving}
                >
                  {t("Annuler")}
                </Button>
                <Button
                  size="sm"
                  color="primary"
                  onPress={onSave}
                  isLoading={is_saving}
                  isDisabled={draft.trim().length === 0}
                >
                  {t("Enregistrer")}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SummaryBlock;
